import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react'
import { useServiceAuth } from '@/contexts/AuthContext'
import type { ParsedData, ParsingError } from '@/interfaces/toolData'
import type { Project } from '@/interfaces'
import { FileService, type ProjectFile } from '@/services/fileService'
import { ParserFactory } from '@/lib/parsers/ParserFactory'
import { getProject } from '@/services/projectService'


// =============================================================================
// Types
// =============================================================================

export interface WaveFile {
  id: string
  name: string
  originalName: string
  projectId: string
  uploadedAt: string
  uploadedBy: string
  fileSize: number
  storagePath: string
  fileType?: string // From parsing results
  parsedData?: ParsedData
  isLoading?: boolean
  processingStatus?: 'fetching' | 'parsing' // Track current processing phase
  error?: ParsingError
  downloadUrl?: string
}

export interface UploadingFile {
  id: string
  name: string
  progress: number // 0-100
  error?: string
}

interface FileState {
  projectId: string | null
  project: Project | null
  files: WaveFile[]
  uploadingFiles: UploadingFile[]
  isLoading: boolean
  error: string | null
}

// =============================================================================
// Actions
// =============================================================================

type FileAction =
  | { type: 'SET_PROJECT'; payload: { id: string; project: Project } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_FILES'; payload: WaveFile[] }
  | { type: 'ADD_FILE'; payload: WaveFile }
  | { type: 'UPDATE_FILE'; payload: { id: string; updates: Partial<WaveFile> } }
  | { type: 'REMOVE_FILE'; payload: string }
  | { type: 'SET_PARSED_DATA'; payload: { fileId: string; data: ParsedData } }
  | { type: 'ADD_UPLOADING_FILE'; payload: UploadingFile }
  | { type: 'UPDATE_UPLOADING_FILE'; payload: { id: string; updates: Partial<UploadingFile> } }
  | { type: 'REMOVE_UPLOADING_FILE'; payload: string }

// =============================================================================
// Reducer
// =============================================================================

const initialState: FileState = {
  projectId: null,
  project: null,
  files: [],
  uploadingFiles: [],
  isLoading: false,
  error: null,
}

function fileReducer(state: FileState, action: FileAction): FileState {
  switch (action.type) {
    case 'SET_PROJECT':
      return { 
        ...state, 
        projectId: action.payload.id,
        project: action.payload.project 
      }
    
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    
    case 'SET_FILES':
      return { ...state, files: action.payload }
    
    case 'ADD_FILE':
      return { ...state, files: [...state.files, action.payload] }
    
    case 'UPDATE_FILE':
      return {
        ...state,
        files: state.files.map(file =>
          file.id === action.payload.id
            ? { ...file, ...action.payload.updates }
            : file
        )
      }
    
    case 'REMOVE_FILE':
      return {
        ...state,
        files: state.files.filter(file => file.id !== action.payload)
      }
    
    case 'SET_PARSED_DATA':
      return {
        ...state,
        files: state.files.map(file => 
          file.id === action.payload.fileId 
            ? { 
                ...file, 
                parsedData: action.payload.data,
                fileType: action.payload.data.fileType,
                isLoading: false,
                processingStatus: undefined,
                error: undefined 
              }
            : file
        ),
      }
    
    case 'ADD_UPLOADING_FILE':
      return { ...state, uploadingFiles: [...state.uploadingFiles, action.payload] }
    
    case 'UPDATE_UPLOADING_FILE':
      return {
        ...state,
        uploadingFiles: state.uploadingFiles.map(file =>
          file.id === action.payload.id
            ? { ...file, ...action.payload.updates }
            : file
        )
      }
    
    case 'REMOVE_UPLOADING_FILE':
      return {
        ...state,
        uploadingFiles: state.uploadingFiles.filter(file => file.id !== action.payload)
      }
    
    default:
      return state
  }
}

// =============================================================================
// Context
// =============================================================================

interface FileContextValue {
  // State
  state: FileState
  
  // Computed values
  parsedFiles: WaveFile[]
  allCurves: Array<{ fileId: string; fileName: string; curves: unknown[] }>
  
  // Actions
  loadProject: (projectId: string) => Promise<void>
  uploadFile: (file: File, options?: { overwriteDuplicates?: boolean }) => Promise<void>
  removeFile: (fileId: string) => Promise<void>
  removeAllFiles: () => Promise<void>
  downloadFile: (fileId: string) => Promise<void>
  
  // Upload progress management
  addUploadingFile: (file: UploadingFile) => void
  updateUploadingFile: (id: string, updates: Partial<UploadingFile>) => void
  removeUploadingFile: (id: string) => void
}

const FileContext = createContext<FileContextValue | null>(null)

// =============================================================================
// Hook
// =============================================================================

export function useFiles() {
  const context = useContext(FileContext)
  if (!context) {
    throw new Error('useFiles must be used within a FileProvider')
  }
  return context
}

// =============================================================================
// Provider
// =============================================================================

interface FileProviderProps {
  children: React.ReactNode
  onProjectNameChange?: (name: string | null) => void
}

export function FileProvider({ children, onProjectNameChange }: FileProviderProps) {
  const [state, dispatch] = useReducer(fileReducer, initialState)
  const getServiceAuth = useServiceAuth()

  // Convert ProjectFile to WaveFile
  const mapProjectFileToWaveFile = useCallback((projectFile: ProjectFile): WaveFile => ({
    id: projectFile.id,
    name: projectFile.name,
    originalName: projectFile.originalName,
    projectId: projectFile.projectId,
    uploadedAt: projectFile.uploadedAt,
    uploadedBy: projectFile.uploadedBy,
    fileSize: projectFile.fileSize,
    storagePath: projectFile.filePath,
    downloadUrl: projectFile.downloadUrl,
  }), [])

  // Parse a single file
  const parseFile = useCallback(async (file: WaveFile) => {
    // Get fresh auth props - no re-renders on token changes
    const serviceAuth = getServiceAuth()
    if (!serviceAuth) {
      console.error('No auth available for parsing')
      return
    }

    console.log(`🔍 Starting parse for file: ${file.name}`)
    dispatch({ type: 'UPDATE_FILE', payload: { id: file.id, updates: { isLoading: true, processingStatus: 'fetching' } } })
    
    try {
      // Split the operation to track fetching vs parsing phases
      const fileService = new FileService(serviceAuth)
      
      // Phase 1: Fetch file content from storage
      console.log(`📥 Fetching file content: ${file.name}`)
      const { content, fileName } = await fileService.getFileContentForParsing(file.id)
      
      // Phase 2: Parse the file content
      console.log(`🔍 Parsing file content: ${file.name}`)
      dispatch({ type: 'UPDATE_FILE', payload: { id: file.id, updates: { processingStatus: 'parsing' } } })
      
      // Call appropriate parseFile overload based on content type
      const parsedData = content instanceof ArrayBuffer 
        ? ParserFactory.parseFile(fileName, content)
        : ParserFactory.parseFile(fileName, content)
      
      console.log(`✅ Successfully parsed file: ${file.name}`, parsedData)
      dispatch({ type: 'SET_PARSED_DATA', payload: { fileId: file.id, data: parsedData } })
      
    } catch (error) {
      console.error(`❌ Parse failed for file: ${file.name}`, error)
      
      let parsingError: ParsingError
      if (error && typeof error === 'object' && 'type' in error && 'message' in error) {
        parsingError = error as ParsingError
      } else {
        parsingError = {
          type: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Parse failed',
          details: error instanceof Error ? error.stack : undefined,
          suggestedAction: 'Please try uploading the file again.'
        }
      }
      
      dispatch({ 
        type: 'UPDATE_FILE', 
        payload: { 
          id: file.id, 
          updates: { isLoading: false, processingStatus: undefined, error: parsingError } 
        } 
      })
    }
  }, [getServiceAuth]) // Function reference never changes - no re-renders

  // Load project and files
  const loadProject = useCallback(async (projectId: string) => {
    // Get fresh auth props - no re-renders on token changes
    const serviceAuth = getServiceAuth()
    if (!serviceAuth) {
      dispatch({ type: 'SET_ERROR', payload: 'No auth available' })
      return
    }

    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    
    try {
      // Fetch project details and files in parallel
      const [projectResult, filesResult] = await Promise.allSettled([
        getProject(projectId, serviceAuth),
        new FileService(serviceAuth).getProjectFiles(projectId, true)
      ])
      
      // Handle project
      if (projectResult.status === 'fulfilled') {
        dispatch({ 
          type: 'SET_PROJECT', 
          payload: { id: projectId, project: projectResult.value } 
        })
        // Notify parent component of project name change
        onProjectNameChange?.(projectResult.value.name)
      }
      
      // Handle files
      if (filesResult.status === 'fulfilled') {
        const waveFiles = filesResult.value.map(mapProjectFileToWaveFile)
        const filesWithLoadingState = waveFiles.map(file => ({
          ...file,
          isLoading: !file.parsedData
        }))
        
        dispatch({ type: 'SET_FILES', payload: filesWithLoadingState })
        
        // Parse files that need it
        const filesToParse = filesWithLoadingState.filter(f => !f.parsedData)
        await Promise.allSettled(filesToParse.map(parseFile))
      } else {
        throw new Error('Failed to load project files')
      }
      
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to load project' 
      })
      // Clear project name on error
      onProjectNameChange?.(null)
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [getServiceAuth, mapProjectFileToWaveFile, parseFile, onProjectNameChange])

  // Upload file
  const uploadFile = useCallback(async (file: File, options?: { overwriteDuplicates?: boolean }) => {
    // Get fresh auth props - no re-renders on token changes
    const serviceAuth = getServiceAuth()
    if (!serviceAuth || !state.projectId) {
      console.error('No auth or project available for file upload')
      return
    }

    try {
      const fileService = new FileService(serviceAuth)
      const uploadResult = await fileService.uploadFile(file, state.projectId, {
        duplicateDetectionLevel: 'name_size',
        ...options
      })
      
      if (!uploadResult.success) {
        if (uploadResult.duplicate) {
          throw new Error(uploadResult.message || 'File already exists')
        } else {
          throw new Error(uploadResult.message || 'Upload failed')
        }
      }
      
      // Add file and parse it
      const waveFile = mapProjectFileToWaveFile(uploadResult.file!)
      waveFile.isLoading = true
      
      dispatch({ type: 'ADD_FILE', payload: waveFile })
      await parseFile(waveFile)
      
    } catch (error) {
      console.error('Upload failed:', error)
      throw error
    }
  }, [getServiceAuth, state.projectId, mapProjectFileToWaveFile, parseFile])

  // Remove file
  const removeFile = useCallback(async (fileId: string) => {
    // Get fresh auth props - no re-renders on token changes
    const serviceAuth = getServiceAuth()
    if (!serviceAuth) {
      console.error('No auth available for file deletion')
      return
    }

    try {
      const fileService = new FileService(serviceAuth)
      await fileService.deleteFile(fileId)
      dispatch({ type: 'REMOVE_FILE', payload: fileId })
    } catch (error) {
      console.error('Remove failed:', error)
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Delete failed' 
      })
    }
  }, [getServiceAuth])

  // Remove all deletable files (success/error status only, not uploading/parsing)
  const removeAllFiles = useCallback(async () => {
    // Get fresh auth props - no re-renders on token changes
    const serviceAuth = getServiceAuth()
    if (!serviceAuth) {
      console.error('No auth available for bulk file deletion')
      return
    }

    // Filter files that can be safely deleted (not uploading or parsing)
    const deletableFiles = state.files.filter(file => 
      file.parsedData !== undefined || file.error !== undefined
    )

    if (deletableFiles.length === 0) {
      console.log('No deletable files found')
      return
    }

    console.log(`Deleting ${deletableFiles.length} files`)

    try {
      const fileService = new FileService(serviceAuth)
      
      // Delete all files in parallel for better performance
      await Promise.allSettled(
        deletableFiles.map(file => fileService.deleteFile(file.id))
      )
      
      // Remove all files from state after deletion
      deletableFiles.forEach(file => {
        dispatch({ type: 'REMOVE_FILE', payload: file.id })
      })
      
    } catch (error) {
      console.error('Bulk delete failed:', error)
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Bulk delete failed' 
      })
    }
  }, [getServiceAuth, state.files])

  // Download file
  const downloadFile = useCallback(async (fileId: string) => {
    // Get fresh auth props - no re-renders on token changes
    const serviceAuth = getServiceAuth()
    if (!serviceAuth) {
      console.error('No auth available for file download')
      return
    }

    try {
      const fileService = new FileService(serviceAuth)
      const downloadUrl = await fileService.getFileDownloadUrl(fileId)
      
      const link = document.createElement('a')
      link.href = downloadUrl
      link.target = '_blank'
      link.click()
    } catch (error) {
      console.error('Download failed:', error)
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Download failed' 
      })
    }
  }, [getServiceAuth])

  // Upload progress management
  const addUploadingFile = useCallback((file: UploadingFile) => {
    dispatch({ type: 'ADD_UPLOADING_FILE', payload: file })
  }, [])

  const updateUploadingFile = useCallback((id: string, updates: Partial<UploadingFile>) => {
    dispatch({ type: 'UPDATE_UPLOADING_FILE', payload: { id, updates } })
  }, [])

  const removeUploadingFile = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_UPLOADING_FILE', payload: id })
  }, [])

  // Computed values
  const parsedFiles = useMemo(
    () => state.files.filter(file => file.parsedData && !file.isLoading),
    [state.files]
  )
  
  const allCurves = useMemo(
    () => parsedFiles.map(file => ({
      fileId: file.id,
      fileName: file.name,
      curves: file.parsedData && 'curves' in file.parsedData 
        ? file.parsedData.curves || [] 
        : []
    })),
    [parsedFiles]
  )

  const contextValue: FileContextValue = {
    state,
    parsedFiles,
    allCurves,
    loadProject,
    uploadFile,
    removeFile,
    removeAllFiles,
    downloadFile,
    addUploadingFile,
    updateUploadingFile,
    removeUploadingFile,
  }

  return (
    <FileContext.Provider value={contextValue}>
      {children}
    </FileContext.Provider>
  )
}